import { axios } from "../../config/axios";
import { anylinkAPI } from "./anylinkAPI";
import { campaignRewardAPI } from "./campaignRewardAPI";
import { beeAPI } from "./beeAPI";

const communicationType = "campaigns";

export const campaignAPI = {
  /**
   * Create digital voucher for Campaign
   * @param {number} campaignId
   * @returns an object containing the reward
   */
  createDigitalVoucher(campaignId) {
    return campaignRewardAPI.create(campaignId, "Campaign");
  },

  /**
   * Will fetch campaign from
   * @param campaignId
   * @returns {Promise<AxiosResponse<any>>}
   */
  fetch(campaignId) {
    return axios.get(`/api/v3/campaigns/${campaignId}`, {
      passthrough: {
        codes: [403]
      }
    }).then((resp) => resp.data);
  },
  /**
   * Will fetch campaign details
   * @param campaignId
   * @returns {Promise<AxiosResponse<any>>}
   */
  fetchDetails(campaignId) {
    return axios.get(`/api/v3/campaigns/${campaignId}/details`, {
      passthrough: {
        codes: [403]
      }
    }).then((resp) => resp.data);
  },
  /**
   * Will fetch campaign stats
   * @param campaignId
   * @param trackingDuration
   * @returns {Promise<AxiosResponse<any>>}
   */
  fetchStats(campaignId, trackingDuration = '2d') {
    return axios.get(`/api/v3/campaigns/${campaignId}/stats`, {
      params: { duration: trackingDuration },
      passthrough: {
        codes: [403]
      }
    }).then((resp) => resp.data);
  },

  /**
   * Get all anylinks from a campaign
   * @param {number} campaignId
   * @returns {array} Array of anylinks ordered by priority
   */
  getAnyLinks(campaignId) {
    return anylinkAPI.getAnyLinks(communicationType, campaignId)
  },
  /**
   * Get digital voucher for campaign
   * @param {number} rewardId
   * @param {number} ownerId
   * @returns an object containing the reward
   */
  getDigitalVoucher(rewardId, ownerId) {
    return campaignRewardAPI.fetch(rewardId, ownerId, 'Campaign');
  },

  /**
   * Get Email Template from a specific campaign
   * @param {Number} campaignId Campaign Id
   * @returns {Object} see BeeAPI doc
   */
  getEmailTemplates(campaignId){
    return beeAPI.getEmailTemplates(campaignId);
  },

  /**
   * Create a new campaign
   * @param {object} params
   * @returns {object}
   */
  createCampaign(params) {
    return axios.post(`/api/v3/campaigns`, params)
                .then(resp => resp.data);
  },

  /**
   * Updates fields on a campaign
   * @param {number} campaignId
   * @param {object} frag
   * @returns
   */
  patch(campaignId, frag) {
    return axios.patch(`/api/v3/campaigns/${campaignId}`, {
      campaign: frag
    }).then((resp) => resp.data)
  },

  /**
   * Updates fields on a reward
   * @param {number} rewardId
   * @param {object} frag
   * @returns
   */
  patchRewards(rewardId, frag) {
    return axios.patch(`/api/v3/campaign_rewards/${campaignId}`, frag)
      .then((resp) => resp.data)
  },

  /**
   * Post Create Campaign with Segments
   * @constructor
   * @param {number} campaignId - The Campaign ID
   * @param {string} clickType - The type of click (campaign_click // no_campaign_click)
   * @returns {object} {edit_path: ""} containing URL edit_path to redirect the user to
  **/
  postCreateCampaignWithSegments(campaignId, clickType){
    return axios.post(`/api/v3/campaigns/${campaignId}/create_campaign_with_segments?segment_type=${clickType}`).then(resp => resp.data);
  },

  /**
   * PUT to update anyLinks for a campaign
   * @param {number} campaignId
   * @param {array} anyLinks
   * @returns {object} status code 200 if successful or error
   */
  putUpdateAnyLinks(campaignId, anyLinks) {
    return anylinkAPI.putUpdateAnyLinks(communicationType, campaignId, anyLinks)
  },

  /**
   * Updates the message for a campaign
   * @param {number} campaignId
   * @param {string} text
   * @returns void
   */
  updateMessage(campaignId, text) {
    return axios.patch(`/api/v3/campaigns/${campaignId}/update_sms_content`, {
      text
    }).then((resp) => resp.data);
  },

  /**
   * Returns an array of all the available groups for a merchant
   * @param number campaignId
   */
  async selectedGroups(campaignId) {
    const resp = await axios.get(`/api/v3/campaigns/${campaignId}/selected_groups?legacy=true`)
    return resp.data;
  },

  /**
   * Updates the selected groups for a campaign
   *
   * @param {number} campaignId
   * @param {{
   *  merchant_segment_ids: Array.<number>
   *  date_range: Date
   * }} params Options for updating groups
   * @returns Promise.<{
   *  groups_with_selected: Array.Object
   *  merchant_segment_ids: Array.number
   *  merchant_tag_ids: Array.Number
   *  sub_configuration: Object.<{ max_date: string, min_date: string }>
   * }>
   */
  async updateGroups(campaignId, params) {
    const resp = await axios.patch(`/api/v3/campaigns/${campaignId}/update_groups`, {
      campaign: params
    });
    return resp.data;
  },

  /**
   * Updates the selected groups for a campaign
   *
   * @param {Integer} campaignId
   * @param {{
   *  tag_type: Integer (enum -> brand: 0, category: 1)
   *  min_date: Date
   *  max_date: Date
   *  min_spend: Number
   *  max_spend: Number
   * }} params Options for updating Campaign Tag Filters
   * @returns Promise.<{
   *  groups_with_selected: Array.Object
   *  merchant_segment_ids: Array.number
   *  merchant_tag_ids: Array.Number
   *  sub_configuration: Object.<{ max_date: string, min_date: string }>
   * }>
   */
  async updateTagSubfilter(campaignId, params) {
    const resp = await axios.patch(`/api/v3/campaigns/${campaignId}/update_tag_subfilter`, {
      tag_subfilter: params
    });
    return resp.data;
  },

  /**
   * @param {number} campaignId
   * @returns Promise.<number>
   */
  async totalRecipientCount(campaignId, key = 'stashboard') {
    const params = {
      key: key
    };

    const resp = await axios.get(`/api/v3/campaigns/${campaignId}/total_count`, { params });
    return resp.data.total_count;
  },

  async sendCampaign(campaignId, status = true) {
    await axios.patch(`/api/v3/campaigns/${campaignId}/update_on_send`, {
      campaign: { status }
    })
  },

  /**
   * Returns kpi data for a campaign
   * @param campaignId
   * @param kpiKey
   * @param dayOfWeek
   * @param {string} commType ('stashboard' | 'sms' | 'push' | 'email')
   */
  async getCampaignKpis(campaignId, kpiKey, dayOfWeek, commType) {
    const params = {selected_day_of_week: dayOfWeek, comm_type: commType};
    const resp = await axios.get(`/api/v3/campaigns/${campaignId}/caddie_kpi/${kpiKey}`, { params })
    return resp.data;
  },

  /**
   * Returns credit and character count information
   * @param {number} campaignId
   */
  async getCredits(campaignId) {
    const resp = await axios.get(`/api/v3/campaigns/${campaignId}/credits`)
    return resp.data;
  },
  /**
   * Returns sending campaigns list depend on list type
   * @param {list_type} listType
   * @oaram {page} pageNumber
   */
  async getCampaigns(listType = '', trackingDuration = '2d', pageNumber = 1) {
    const resp = await axios.get(`/api/v3/campaigns/?list_type=${listType}&page=${pageNumber}&duration=${trackingDuration}`)
    return resp.data
  },
}
